import NorthEastRoundedIcon from '@mui/icons-material/NorthEastRounded'
import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import Link from 'next/link'
import React from 'react'
import ConfigurableAnimatedSection from 'src/components/AnimatedSection'
import BaseButton from 'src/components/Buttons/BaseButton'
import { PILLAR_NS_MAP, PillarPage } from 'src/constants/searchPages'
import { RedirectUrls } from 'src/types/types'

interface ISizeCard {
  i18nKey: string
  image: string
  sizeFilter: string
}

const STORAGE_SIZES: ISizeCard[] = [
  {
    i18nKey: 'XSMALL',
    image: '/images/sizes/small/small-closet-v2.webp',
    sizeFilter: 'XSMALL'
  },
  {
    i18nKey: 'SMALL',
    image: '/images/sizes/small/small-studio-v2.webp',
    sizeFilter: 'SMALL'
  },
  {
    i18nKey: 'MEDIUM',
    image: '/images/sizes/medium/medium-2-bedroom-v2.webp',
    sizeFilter: 'MEDIUM'
  },
  {
    i18nKey: 'LARGE',
    image: '/images/sizes/large/large-3-bedroom-v2.webp',
    sizeFilter: 'LARGE'
  }
]

const PARKING_SIZES: ISizeCard[] = [
  {
    i18nKey: 'MOTO',
    image: '/images/sizes/vehicle/moto-v2.webp',
    sizeFilter: 'PARKING'
  },
  {
    i18nKey: 'AUTO',
    image: '/images/sizes/vehicle/auto-compact-v2.webp',
    sizeFilter: 'PARKING'
  },
  {
    i18nKey: 'CAMIONETA',
    image: '/images/sizes/vehicle/suv-v2.webp',
    sizeFilter: 'PARKING'
  },
  {
    i18nKey: 'FLOTILLA',
    image: '/images/sizes/flotilla/van-v2.webp',
    sizeFilter: 'PARKING'
  }
]

interface IProps {
  pillar: PillarPage
  ctaHref: string
}

const SizeSection = ({ pillar, ctaHref }: IProps) => {
  const { t } = useTranslation(PILLAR_NS_MAP[pillar])
  const { t: tCommon } = useTranslation('common')
  const sizes = pillar === PillarPage.Parking ? PARKING_SIZES : STORAGE_SIZES

  return (
    <ConfigurableAnimatedSection
      variant="slideUp"
      className="border-t border-ink-100/15 bg-cream-300 py-20 md:py-28"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="md:flex md:items-end md:justify-between md:gap-8">
          <div>
            <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
              {tCommon('PILLAR_SECTIONS.EYEBROW_SIZES')}
            </p>
            <h2 className="mt-3 max-w-2xl font-display text-[26px] leading-[1.15] font-medium text-ink-700 md:text-[40px]">
              {t('STEPS.TITLE')}
            </h2>
            <p className="mt-4 max-w-lg text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
              {t('STEPS.SUBTITLE_1')}
            </p>
          </div>
          <Link
            href={RedirectUrls.SizeGuide}
            className="mt-5 hidden shrink-0 text-[13px] font-medium tracking-[0.08em] text-ink-700 uppercase underline decoration-blue-500/40 underline-offset-[6px] transition-colors hover:text-blue-500 hover:decoration-blue-500 md:mt-0 md:inline-block"
          >
            {t('STEPS.SIZE_GUIDE_LINK')}
          </Link>
        </div>

        <ul className="scrollbar-hide mt-8 flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2 md:mt-10 md:grid md:snap-none md:grid-cols-4 md:gap-5 md:overflow-visible md:pb-0">
          {sizes.map((size, i) => {
            const stepNumber = String(i + 1).padStart(2, '0')
            return (
              <li
                key={size.i18nKey}
                className="w-[220px] shrink-0 snap-start md:w-auto"
              >
                <Link
                  href={`${RedirectUrls.Search}?sizes=${size.sizeFilter}`}
                  className="group flex h-full flex-col overflow-hidden border border-ink-100/12 bg-white-500 transition-colors duration-300 hover:border-blue-500/40 hover:bg-cream-200"
                >
                  <div className="relative aspect-[5/4] overflow-hidden bg-cream-200">
                    <Image
                      src={size.image}
                      alt={t(`STEPS.SIZES.${size.i18nKey}.TITLE` as never)}
                      fill
                      sizes="(max-width: 900px) 220px, 25vw"
                      style={{ objectFit: 'contain' }}
                      quality={85}
                      className="p-5 transition-transform duration-500 group-hover:scale-[1.03]"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex flex-1 flex-col px-4 py-4 md:px-5 md:py-5">
                    <p className="font-display text-[11px] tracking-[0.18em] text-blue-500 uppercase">
                      <span className="italic">{stepNumber}</span>
                      <span className="mx-2 text-ink-100/40">·</span>
                      <span className="not-italic">
                        {t(`STEPS.SIZES.${size.i18nKey}.META` as never)}
                      </span>
                    </p>
                    <h3 className="mt-2 font-display text-[18px] leading-[1.2] font-medium text-ink-700 md:text-[20px]">
                      {t(`STEPS.SIZES.${size.i18nKey}.TITLE` as never)}
                    </h3>
                    <p className="mt-2 text-[13px] leading-relaxed text-ink-200">
                      {t(`STEPS.SIZES.${size.i18nKey}.DESC` as never)}
                    </p>
                    <span className="mt-4 inline-flex items-center gap-1 text-[11px] tracking-[0.08em] text-blue-500 uppercase transition-colors group-hover:text-ink-700">
                      {t('STEPS.BUTTON_PHOLDER')}
                      <NorthEastRoundedIcon
                        className="text-[14px] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                        fontSize="inherit"
                      />
                    </span>
                  </div>
                </Link>
              </li>
            )
          })}
        </ul>

        <div className="mt-12 flex justify-start md:mt-14">
          <BaseButton
            fontSize={14}
            size="medium"
            color="primary"
            variant="contained"
            rounded
            href={ctaHref}
          >
            {t('STEPS.BUTTON_PHOLDER')}
          </BaseButton>
        </div>
      </div>
    </ConfigurableAnimatedSection>
  )
}

export default React.memo(SizeSection)
